import { OnnxProcessModelComponent } from './components/onnx-process-model/onnx-process-model';
import { ProcessParameterDefaultsComponent } from './components/process-parameter-defaults/process-parameter-defaults';
import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  TemplateRef,
  computed,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { toObservable } from '@angular/core/rxjs-interop';
import { EMPTY, Subject, catchError, debounceTime, distinctUntilChanged, finalize, forkJoin, map, of, skip, switchMap, tap } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DialogModule } from 'primeng/dialog';
import { TagModule } from 'primeng/tag';
import { BackButton } from '@components/ui/back-button/back-button';
import { Loading } from '@components/ui/loading/loading';
import { PrimaryButton } from '@components/button/primary-button/primary-button';
import { SecondaryButton } from '@components/button/secondary-button/secondary-button';
import { TabItem, TabsDynamic } from '@components/ui/tabs/tabs-dynamic';
import { DatePickerComponent } from '@components/date-picker/date-picker.component';
import { DateFormatLangPipe } from '@app/utils/pipes/date-format-lang-pipe';
import { DataTableComponent, TableColumn, TableConfig } from '@components/data-table/data-table.component';
import { CustomPaginatorComponent } from '@app/pages/Ui-Kit/custom-paginator/custom-paginator.component';
import { MsfInput } from '@components/ui/forms/input/input';
import { MsfSelect } from '@components/select/select';
import { WorkerTagPickerComponent } from '@app/pages/admin/worker-tags-page/worker-tag-picker/worker-tag-picker';
import { CheckboxComponent } from '@components/checkbox/checkbox.component';
import { WorkerTagsService } from '@app/services/worker-tags.service';
import { ProcessFailurePolicyService } from '@app/services/process-failure-policy.service';
import { ProcessExecutionHoldService } from '@app/services/process-execution-hold.service';
import { RestartFailedAssignmentsService } from '@app/services/restart-failed-assignments.service';
import { workerTagOptions } from '@app/pages/admin/worker-tags-page/worker-tags.util';
import {
  INTERRUPTION_RESUME_SAME,
  INTERRUPTION_WAIT_FOR_NEXT,
} from '@app/pages/admin/process-subscription-form.util';
import { AssignmentProcessService as AssignmentProcessApiService } from '@6grain/masofa-monolith-api-client/common/api/assignmentProcess.service';
import { OrchestrationResourceProfileService as OrchestrationResourceProfileApiService } from '@6grain/masofa-monolith-api-client/common/api/orchestrationResourceProfile.service';
import { OrchestrationResourceProfileResponse } from '@6grain/masofa-monolith-api-client/common/model/orchestrationResourceProfileResponse';
import { QueueParallelismRequest } from '@6grain/masofa-monolith-api-client/common/model/queueParallelismRequest';
import { SaveOrchestrationResourceProfileRequest } from '@6grain/masofa-monolith-api-client/common/model/saveOrchestrationResourceProfileRequest';
import {
  applyCapToOverlay,
  applyTagsToOverlay,
  findActiveSatelliteProfile,
  overlayCapInput,
  readProfileWorkerTags,
  tagSelectionForProcess,
} from '@app/pages/admin/orchestration-resources-page/orchestration-resources-page.util';
import { ProcessSubscriptionService as ProcessSubscriptionApiService } from '@6grain/masofa-monolith-api-client/common/api/processSubscription.service';
import { ProcessEventTriggerService as ProcessEventTriggerApiService } from '@6grain/masofa-monolith-api-client/common/api/processEventTrigger.service';
import { MonitoredOperationListItemResponse } from '@6grain/masofa-monolith-api-client/common/model/monitoredOperationListItemResponse';
import { StartAssignmentProcessRequest } from '@6grain/masofa-monolith-api-client/common/model/startAssignmentProcessRequest';
import { operationDetailsUrl } from '@app/pages/admin/operation-details.util';
import { NOTIFY_PEOPLE_PATH, notifyPeopleQueryParams } from '@app/pages/admin/orchestration-notify-people.util';
import { developerCatalogCommandLink } from '@app/pages/admin/developer-catalog-page/developer-catalog.utils';
import { isCodeCatalogEnabled } from '@app/utils/environment-features';
import { ProcessDataEffect, dataEffectOperationSeverity } from '@app/pages/admin/orchestration-data-records.util';
import { ProcessStorageEffect } from '@app/pages/admin/orchestration-object-storage.util';
import { assignmentStartApiError } from '@app/pages/admin/assignment-start.util';
import {
  canonicalProcessKey,
  isGeometryProcessKey,
} from '@app/pages/admin/orchestration-process-keys.util';
import { OrchestrationIdentityCellComponent } from '@app/pages/admin/components/orchestration-identity-cell/orchestration-identity-cell';
import { CopyableIdentifierComponent } from '@app/pages/admin/components/copyable-identifier/copyable-identifier';
import { CommandConfigLinksComponent } from '@app/pages/admin/orchestration-command-config-page/command-config-links';
import { ProcessAdmissionFactsComponent } from '@app/pages/admin/components/process-admission-facts/process-admission-facts';
import { formatQuartzCronLabel } from '@app/pages/admin/orchestration-quartz-cron.util';
import { formatTimeZoneLabel } from '@app/pages/admin/orchestration-time-zones.util';
import {
  OrchestrationIdentityLine,
  assignmentRowTitle,
  buildTriggerIdentityLines,
  namedKeyLabel,
} from '@app/pages/admin/orchestration-identity.util';
import { filterByCatalogQuery } from '@app/pages/admin/orchestration-catalog-search.util';
import { OrchestrationTableHeadingComponent } from '@app/pages/admin/components/orchestration-table-heading/orchestration-table-heading';
import { AssignmentStartFormComponent } from './components/assignment-start-form/assignment-start-form';
import { adminHashesEqual } from '@app/pages/admin/admin-hash-state';
import { toRecordPageHeading } from '@app/pages/admin/orchestration-record-heading.util';
import { OperationMonitoringService as OperationMonitoringApiService } from '@6grain/masofa-monolith-api-client/common/api/operationMonitoring.service';
import {
  AssignmentResultFilter,
  assignmentResultFilterAfterCreate,
  assignmentResultFilterQuery,
  formatAssignmentResultTooltip,
  readAssignmentResultCount,
} from '@app/pages/admin/assignment-result-count.util';
import { formatAssignmentDuration } from '@app/pages/admin/assignment-duration.util';
import { assignmentOutcome } from '@app/pages/admin/assignment-execution.util';
import { assignmentStatusAt } from '@app/pages/admin/assignment-status-at.util';
import { assignmentHostLabel, readAssignmentHost, readAssignmentWorker } from '@app/pages/admin/assignment-host.util';
import { AssignmentHostLabelComponent } from '@app/pages/admin/components/assignment-host-label/assignment-host-label';
import { AssignmentResultFilterComponent } from '@app/pages/admin/components/assignment-result-filter/assignment-result-filter';
import { AssignmentResultCellComponent } from '@app/pages/admin/components/assignment-result-cell/assignment-result-cell';
import { AssignmentRssCellComponent } from '@app/pages/admin/components/assignment-rss-cell/assignment-rss-cell';
import { AssignmentRssView, readAssignmentRss } from '@app/pages/admin/assignment-rss';
import { AssignmentProcessGroupsService } from '@app/services/assignment-process-groups.service';
import { OrchestrationProcessEventsComponent } from '@app/pages/admin/components/orchestration-process-events/orchestration-process-events';
import { OrchestrationProcessActivityComponent } from '@app/pages/admin/components/orchestration-process-activity/orchestration-process-activity';
import { indexProcessActivity, ProcessActivityView } from '@app/pages/admin/orchestration-process-activity.util';
import { ProcessEventChipView, publishedEventsForProcess, sourceEventKeysByProcess } from '@app/pages/admin/orchestration-process-events.util';
import { notificationRuleUrl } from '@app/pages/admin/admin-notifications-routes.util';
import { notificationTemplateFieldEvents, notificationTemplatesForEvents, processHasCompletionTemplate } from '@app/pages/admin/notification-template-fields.util';
import { NotificationTemplateFieldsComponent } from '@app/pages/admin/components/notification-template-fields/notification-template-fields';
import {
  NotifiableEventDto,
  NotificationRuleDto,
  NotificationTemplateDto,
  UserNotificationsApiService,
} from '@app/services/user-notifications-api.service';
import { AccessProcessGrantsService } from '@app/services/access-process-grants.service';
import { AccessGrantRow } from '@6grain/masofa-monolith-api-client/identity/model/accessGrantRow';
import {
  ASSIGNMENT_STATE_FILTERS,
  AssignmentStateFilter,
  assignmentOperationsStateQuery,
  ProcessDefinition,
  TagSeverity,
  TriggerDirection,
  assignmentStateSeverity,
  canCancelAssignment,
  canRestartAssignment,
  createdRangeApiBounds,
  filterAssignmentsByProcessKey,
  filterSubscriptionsByProcessKey,
  filterTriggersByProcessKey,
  findProcessDefinition,
  notificationRulesForProcess,
  notificationSubscriberLabel,
  processPageTabFromHash,
  processPageTabHash,
  triggerDirection,
} from './orchestration-process-page.util';

interface DataEffectRow extends ProcessDataEffect {
  commandLabel: string;
  commandUrl: string[] | null;
  operationSeverity: TagSeverity;
}

interface StorageEffectRow extends ProcessStorageEffect {
  commandLabel: string;
  commandUrl: string[] | null;
  operationSeverity: TagSeverity;
}

interface AssignmentStateChip {
  id: AssignmentStateFilter;
  label: string;
  selected: boolean;
}

interface AssignmentRow {
  id: string;
  feedName: string;
  operationType: string;
  name: string;
  state: string;
  stateLabel: string;
  stateSeverity: TagSeverity;
  statusAt: string | null;
    createdAt?: string;
    progressLabel: string;
    durationLabel: string;
    hostLabel: string;
    workerId: string | null;
    workerServiceName: string | null;
    workerHostName: string | null;
    resultCount: number | null;
    resultTooltip: string | null;
    rss: AssignmentRssView | null;
    error: string | null;
  canCancel: boolean;
  canRestart: boolean;
}

interface SubscriptionRow {
  id: string;
  name: string;
  cronExpression: string;
  timeZoneId: string;
  isActive: boolean;
  statusLabel: 'Active' | 'Inactive';
  statusSeverity: TagSeverity;
  nextFireAt?: string | null;
  lastTriggeredAt?: string | null;
  lastAssignmentId: string | null;
  lastAssignmentUrl: string | null;
}

interface TriggerRow {
  id: string;
  name: string;
  identityLines: OrchestrationIdentityLine[];
  direction: TriggerDirection;
  isActive: boolean;
  statusLabel: 'Active' | 'Inactive';
  statusSeverity: TagSeverity;
  executionCount: number;
  successfulExecutionCount: number;
  failedExecutionCount: number;
  lastAssignmentId: string | null;
  lastAssignmentUrl: string | null;
}

@Component({
  selector: 'app-orchestration-process-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    TranslateModule,
    DialogModule,
    TagModule,
    BackButton,
    Loading,
    PrimaryButton,
    SecondaryButton,
    TabsDynamic,
    DatePickerComponent,
    DateFormatLangPipe,
    DataTableComponent,
    CustomPaginatorComponent,
    AssignmentStartFormComponent,
    OnnxProcessModelComponent,
    ProcessParameterDefaultsComponent,
    OrchestrationTableHeadingComponent,
    OrchestrationIdentityCellComponent,
    AssignmentResultFilterComponent,
    AssignmentResultCellComponent,
    AssignmentRssCellComponent,
    OrchestrationProcessEventsComponent,
    OrchestrationProcessActivityComponent,
    CopyableIdentifierComponent,
    AssignmentHostLabelComponent,
    CommandConfigLinksComponent,
    ProcessAdmissionFactsComponent,
    MsfInput,
    MsfSelect,
    WorkerTagPickerComponent,
    CheckboxComponent,
    NotificationTemplateFieldsComponent,
  ],
  templateUrl: './orchestration-process-page.html',
  styleUrl: './orchestration-process-page.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DatePipe],
})
export class OrchestrationProcessPageComponent implements OnInit, OnDestroy {
  private readonly assignmentsApi = inject(AssignmentProcessApiService);
  private readonly resourceProfilesApi = inject(OrchestrationResourceProfileApiService);
  private readonly workerTagsApi = inject(WorkerTagsService);
  private readonly failurePolicyApi = inject(ProcessFailurePolicyService);
  private readonly executionHoldApi = inject(ProcessExecutionHoldService);
  private readonly restartFailedApi = inject(RestartFailedAssignmentsService);
  private readonly monitoringApi = inject(OperationMonitoringApiService);
  private readonly groupsApi = inject(AssignmentProcessGroupsService);
  private readonly subscriptionsApi = inject(ProcessSubscriptionApiService);
  private readonly triggersApi = inject(ProcessEventTriggerApiService);
  private readonly notificationsApi = inject(UserNotificationsApiService);
  private readonly accessGrantsApi = inject(AccessProcessGrantsService);
  private readonly datePipe = inject(DatePipe);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  readonly translate = inject(TranslateService);
  private readonly destroy$ = new Subject<void>();
  private readonly reload$ = new Subject<string>();
  private readonly assignmentsReload$ = new Subject<void>();
  private readonly processRssReload$ = new Subject<string>();
  private readonly processCatalog = signal<ProcessDefinition[]>([]);

  private readonly assignmentsTemplate = viewChild<TemplateRef<unknown>>('assignmentsTemplate');
  private readonly subscriptionsTemplate = viewChild<TemplateRef<unknown>>('subscriptionsTemplate');
  private readonly triggersTemplate = viewChild<TemplateRef<unknown>>('triggersTemplate');
  private readonly databaseTemplate = viewChild<TemplateRef<unknown>>('databaseTemplate');
  private readonly templatesTemplate = viewChild<TemplateRef<unknown>>('templatesTemplate');
  private readonly subscribersTemplate = viewChild<TemplateRef<unknown>>('subscribersTemplate');
  private readonly accessTemplate = viewChild<TemplateRef<unknown>>('accessTemplate');
  private readonly assignmentNameTemplate = viewChild<TemplateRef<unknown>>('assignmentNameTemplate');
  private readonly assignmentHostTemplate = viewChild<TemplateRef<unknown>>('assignmentHostTemplate');
  private readonly assignmentStateTemplate = viewChild<TemplateRef<unknown>>('assignmentStateTemplate');
  private readonly assignmentCountTemplate = viewChild<TemplateRef<unknown>>('assignmentCountTemplate');
  private readonly assignmentRssTemplate = viewChild<TemplateRef<unknown>>('assignmentRssTemplate');
  private readonly statusTagTemplate = viewChild<TemplateRef<unknown>>('statusTagTemplate');
  private readonly lastAssignmentTemplate = viewChild<TemplateRef<unknown>>('lastAssignmentTemplate');
  private readonly triggerIdentityTemplate = viewChild<TemplateRef<unknown>>('triggerIdentityTemplate');
  private readonly triggerDirectionTemplate = viewChild<TemplateRef<unknown>>('triggerDirectionTemplate');
  private readonly triggerExecutionsTemplate = viewChild<TemplateRef<unknown>>('triggerExecutionsTemplate');
  private readonly assignmentActionsTemplate = viewChild<TemplateRef<unknown>>('assignmentActionsTemplate');
  private readonly dataEffectCommandTemplate = viewChild<TemplateRef<unknown>>('dataEffectCommandTemplate');
  private readonly dataEffectOperationTemplate = viewChild<TemplateRef<unknown>>('dataEffectOperationTemplate');
  private readonly dataEffectDescriptionTemplate = viewChild<TemplateRef<unknown>>('dataEffectDescriptionTemplate');
  private readonly startForm = viewChild(AssignmentStartFormComponent);

  readonly processKey = signal('');
  readonly process = signal<ProcessDefinition | null>(null);
  readonly processActivity = signal<ProcessActivityView | null>(null);
  readonly eventCatalog = signal<Array<{ key: string; displayName?: string }>>([]);
  readonly notifiableEvents = signal<NotifiableEventDto[]>([]);
  readonly notificationRules = signal<NotificationRuleDto[]>([]);
  readonly notificationTemplates = signal<NotificationTemplateDto[]>([]);
  readonly sourceEventKeys = signal<string[]>([]);
  readonly loading = signal(false);
  readonly notFound = signal(false);
  readonly errorMessage = signal('');
  readonly activeTab = signal('assignments');
  private applyingTabHash = false;
  readonly assignmentRows = signal<AssignmentRow[]>([]);
  readonly processRss = signal<AssignmentRssView | null>(null);
  readonly processRssReady = signal(false);
  readonly subscriptionRows = signal<SubscriptionRow[]>([]);
  readonly triggerRows = signal<TriggerRow[]>([]);
  readonly stateFilter = signal<AssignmentStateFilter>('all');
  readonly resultFilter = signal<AssignmentResultFilter>('positive');
  readonly createdRange = signal<Date[] | null>(null);
  readonly assignmentSearch = signal(this.route.snapshot.queryParamMap.get('assignments') ?? '');
  private readonly assignmentSearchChanges$ = toObservable(this.assignmentSearch).pipe(
    debounceTime(300),
    map(value => value.trim()),
    distinctUntilChanged(),
    skip(1),
  );
  readonly assignmentPage = signal(1);
  readonly assignmentPageSize = signal(25);
  readonly assignmentTotal = signal(0);
  readonly assignmentFirst = signal(0);
  readonly assignmentLoading = signal(false);
  readonly assignmentPageSizeOptions = [10, 25, 50, 100];
  readonly subscriptionSearch = signal('');
  readonly subscriberSearch = signal('');
  readonly accessRows = signal<AccessGrantRow[]>([]);
  readonly accessColumns: TableColumn[] = [
    { field: 'subjectType', header: 'access.who', width: '140px' },
    { field: 'subjectKey', header: 'access.subjectKey' },
    { field: 'permissionCode', header: 'Permission', width: '220px' },
    { field: 'effect', header: 'access.effect', width: '120px' },
    { field: 'scopeType', header: 'access.scope', width: '140px' },
  ];
  readonly accessTableConfig: TableConfig = { paginator: false, emptyMessage: 'access.process.empty' };
  readonly triggerSearch = signal('');
  readonly dataEffectSearch = signal('');
  readonly storageEffectSearch = signal('');
  readonly cancellationVisible = signal(false);
  readonly cancellationSaving = signal(false);
  readonly cancellationAssignmentId = signal<string | null>(null);
  readonly stopAllVisible = signal(false);
  readonly stopAllSaving = signal(false);
  readonly executionHoldVisible = signal(false);
  readonly executionHoldSaving = signal(false);
  readonly executionSuspended = computed(() => this.process()?.executionSuspended === true);
  readonly executionHoldTitle = computed(() => this.executionSuspended() ? 'Resume process' : 'Stop process');
  readonly executionHoldConfirmation = computed(() => this.executionSuspended()
    ? 'Resume process confirmation'
    : 'Stop process confirmation');
  readonly executionHoldIcon = computed(() => this.executionSuspended() ? 'pi pi-play' : 'pi pi-pause');
  readonly executionHoldDismissTitle = computed(() => this.executionSuspended() ? 'Keep stopped' : 'Keep running');
  readonly restartFailedVisible = signal(false);
  readonly restartFailedSaving = signal(false);
  readonly capVisible = signal(false);
  readonly capLoading = signal(false);
  readonly capSaving = signal(false);
  readonly capInput = signal('');
  readonly capError = signal('');
  readonly capProfile = signal<OrchestrationResourceProfileResponse | null>(null);
  readonly tagsVisible = signal(false);
  readonly tagsLoading = signal(false);
  readonly tagsSaving = signal(false);
  readonly tagsSelected = signal<string[]>([]);
  readonly tagCatalog = signal<string[]>([]);
  readonly tagOptions = computed(() => workerTagOptions(this.tagCatalog(), this.tagsSelected()));
  readonly tagsAnyWorker = signal(false);
  readonly tagsError = signal('');
  readonly tagsProfile = signal<OrchestrationResourceProfileResponse | null>(null);
  readonly failureVisible = signal(false);
  readonly failureSaving = signal(false);
  readonly failureError = signal('');
  readonly failurePolicy = signal(INTERRUPTION_WAIT_FOR_NEXT);
  readonly failureResume = signal(1);
  readonly failurePause = signal(0);
  readonly failureOptions = [
    { id: INTERRUPTION_WAIT_FOR_NEXT, label: 'Wait for the next run' },
    { id: INTERRUPTION_RESUME_SAME, label: 'Continue the same assignment' },
  ];
  readonly failureShowsResume = computed(() => this.failurePolicy() === INTERRUPTION_RESUME_SAME);
  readonly failurePolicyLabel = computed(() =>
    this.process()?.interruptionPolicy === INTERRUPTION_RESUME_SAME
      ? 'Continue the same assignment'
      : 'Wait for the next run');
  readonly failureResumeLimit = computed(() => this.process()?.resumeAttemptLimit ?? 1);
  readonly failurePauseAfter = computed(() => this.process()?.pauseAfterConsecutiveFailures ?? 0);
  readonly restartVisible = signal(false);
  readonly restartSaving = signal(false);
  readonly restartAssignmentId = signal<string | null>(null);
  readonly createVisible = signal(false);
  readonly createSaving = signal(false);
  readonly createError = signal('');

  readonly canCreateAssignment = computed(() => this.startForm()?.ready() === true);

  readonly pageHeading = computed(() => toRecordPageHeading({
    meaning: this.process()?.displayName,
    key: this.process()?.key,
    fallbackTitle: 'Process details',
  }));

  readonly publishedEvents = computed<ProcessEventChipView[]>(() => {
    const processKey = this.process()?.key;
    if (!processKey) {
      return [];
    }
    return publishedEventsForProcess(processKey, this.eventCatalog(), this.sourceEventKeys());
  });

  readonly templateFieldEvents = computed(() => {
    const domainKeys = this.publishedEvents()
      .filter(event => event.kind === 'domain')
      .map(event => event.eventKey);
    const processKey = this.process()?.key ?? '';
    const events = notificationTemplateFieldEvents(
      this.notifiableEvents(),
      [...domainKeys, 'assignment.failed', ...(processHasCompletionTemplate(processKey) ? ['assignment.completed'] : [])],
      true,
    );
    return notificationTemplatesForEvents(
      events,
      this.notificationRules(),
      this.notificationTemplates(),
      processKey,
    );
  });

  readonly templateRows = computed(() =>
    this.templateFieldEvents().flatMap(event =>
      event.templates.map(template => ({
        id: `${event.key}:${template.id}`,
        templateId: template.id,
        eventLabel: event.displayName,
        name: template.name,
        lines: template.lines,
        tokens: event.tokens,
        lists: event.lists,
      })),
    ),
  );

  readonly expandedTemplateRowId = signal<string | null>(null);

  readonly templateColumns: TableColumn[] = [
    {
      field: 'eventLabel',
      header: 'Event',
      type: 'text',
      parseFunction: row => this.translate.instant(row.eventLabel),
    },
    { field: 'name', header: 'Template', type: 'text' },
    { field: 'actions', header: '', type: 'actions', width: '80px' },
  ];

  readonly templateTableConfig: TableConfig = {
    paginator: false,
    keepOrder: true,
    emptyMessage: 'No notification templates for this event',
    actions: [
      {
        icon: 'pi pi-pencil',
        tooltip: 'Edit template',
        command: row => this.expandedTemplateRowId.set(row.templateId),
      },
    ],
  };

  readonly subscriberRows = computed(() =>
    notificationRulesForProcess(this.process()?.key ?? this.processKey(), this.notificationRules()).map(rule => ({
      id: rule.id,
      name: rule.name?.trim() || '—',
      eventKey: rule.eventKey,
      recipients: notificationSubscriberLabel(rule.recipients) || '—',
      channels: rule.channels.join(', ') || '—',
      isActive: rule.isActive,
    })),
  );

  readonly visibleSubscribers = computed(() =>
    filterByCatalogQuery(
      this.subscriberRows(),
      this.subscriberSearch(),
      row => [row.name, row.eventKey, row.recipients, row.channels],
    ),
  );

  readonly subscriberColumns: TableColumn[] = [
    { field: 'eventKey', header: 'Event', type: 'text' },
    { field: 'name', header: 'Name', type: 'text' },
    { field: 'recipients', header: 'Notification recipients', type: 'text', minWidth: '14rem' },
    { field: 'channels', header: 'Channels', type: 'text' },
    { field: 'isActive', header: 'Active', type: 'boolean' },
  ];

  readonly subscriberTableConfig: TableConfig = {
    paginator: false,
    keepOrder: true,
    emptyMessage: 'No notification subscribers',
  };

  readonly visibleSubscriptions = computed(() =>
    filterByCatalogQuery(
      this.subscriptionRows(),
      this.subscriptionSearch(),
      row => [row.name, row.cronExpression, row.timeZoneId, row.statusLabel, row.lastAssignmentId],
    ),
  );

  readonly visibleTriggers = computed(() =>
    filterByCatalogQuery(
      this.triggerRows(),
      this.triggerSearch(),
      row => [
        row.name,
        row.direction,
        row.statusLabel,
        row.lastAssignmentId,
        ...row.identityLines.flatMap(line => [line.label, line.title]),
      ],
    ),
  );

  readonly stateChips = computed<AssignmentStateChip[]>(() => {
    const selected = this.stateFilter();
    return ASSIGNMENT_STATE_FILTERS.map(id => ({
      id,
      label: id === 'all' ? 'All' : id,
      selected: selected === id,
    }));
  });

  readonly assignmentTableConfig = computed<TableConfig>(() => ({
    paginator: false,
    keepOrder: true,
    emptyMessage: this.assignmentSearch().trim()
      ? 'No catalog matches'
      : 'No assignments for this process',
  }));

  readonly dataEffectRows = computed<DataEffectRow[]>(() =>
    (this.process()?.dataEffects ?? []).map(effect => ({
      ...effect,
      commandLabel: effect.command === '*' ? 'All commands' : effect.command,
      commandUrl: isCodeCatalogEnabled() && effect.command && effect.command !== '*'
        ? developerCatalogCommandLink(effect.command)
        : null,
      operationSeverity: dataEffectOperationSeverity(effect.operation),
    })),
  );

  readonly storageEffectRows = computed<StorageEffectRow[]>(() =>
    (this.process()?.storageEffects ?? []).map(effect => ({
      ...effect,
      commandLabel: effect.command === '*' ? 'All commands' : effect.command,
      commandUrl: isCodeCatalogEnabled() && effect.command && effect.command !== '*'
        ? developerCatalogCommandLink(effect.command)
        : null,
      operationSeverity: dataEffectOperationSeverity(effect.operation),
    })),
  );

  readonly visibleDataEffects = computed(() =>
    filterByCatalogQuery(
      this.dataEffectRows(),
      this.dataEffectSearch(),
      row => [row.command, row.commandLabel, row.database, row.table, row.operation, row.description],
    ),
  );

  readonly visibleStorageEffects = computed(() =>
    filterByCatalogQuery(
      this.storageEffectRows(),
      this.storageEffectSearch(),
      row => [row.command, row.commandLabel, row.bucket, row.objectKind, row.operation, row.description],
    ),
  );

  readonly detailsTabs = computed<TabItem[]>(() => {
    const assignments = this.assignmentsTemplate();
    const subscriptions = this.subscriptionsTemplate();
    const triggers = this.triggersTemplate();
    const database = this.databaseTemplate();
    const templates = this.templatesTemplate();
    const subscribers = this.subscribersTemplate();
    const access = this.accessTemplate();
    if (!assignments || !subscriptions || !triggers || !database || !templates || !subscribers || !access) {
      return [];
    }
    return [
      { id: 'assignments', label: 'Assignments', count: this.assignmentTotal(), template: assignments },
      { id: 'subscriptions', label: 'Subscriptions', count: this.subscriptionRows().length, template: subscriptions },
      { id: 'triggers', label: 'Event triggers', count: this.triggerRows().length, template: triggers },
      { id: 'templates', label: 'Templates', count: this.templateRows().length, template: templates },
      { id: 'subscribers', label: 'Notification subscribers', count: this.subscriberRows().length, template: subscribers },
      { id: 'storage', label: 'Storage', count: this.dataEffectRows().length + this.storageEffectRows().length, template: database },
      { id: 'access', label: 'access.tab.grants', count: this.accessRows().length, template: access },
    ];
  });

  readonly assignmentColumns = computed<TableColumn[]>(() => [
    {
      field: 'name',
      header: 'Assignment name',
      type: 'custom',
      customTemplate: this.assignmentNameTemplate(),
    },
    {
      field: 'state',
      header: 'Status',
      type: 'custom',
      customTemplate: this.assignmentStateTemplate(),
    },
    {
      field: 'hostLabel',
      header: 'Executed on',
      type: 'custom',
      customTemplate: this.assignmentHostTemplate(),
    },
    { field: 'createdAt', header: 'Created', type: 'date', dateTime: true },
    { field: 'progressLabel', header: 'Progress', type: 'text' },
    { field: 'durationLabel', header: 'Duration', type: 'text' },
    {
      field: 'resultCount',
      header: 'Result',
      type: 'custom',
      customTemplate: this.assignmentCountTemplate(),
    },
    {
      field: 'rss',
      header: 'RSS',
      type: 'custom',
      customTemplate: this.assignmentRssTemplate(),
    },
    {
      field: 'actions',
      header: 'Actions',
      type: 'custom',
      customTemplate: this.assignmentActionsTemplate(),
    },
  ]);

  readonly subscriptionColumns = computed<TableColumn[]>(() => [
    { field: 'name', header: 'Name', type: 'text' },
    { field: 'cronExpression', header: 'Schedule', type: 'text' },
    { field: 'timeZoneId', header: 'Time zone', type: 'text' },
    {
      field: 'statusLabel',
      header: 'Status',
      type: 'custom',
      customTemplate: this.statusTagTemplate(),
    },
    { field: 'nextFireAt', header: 'Next run', type: 'date', dateTime: true },
    { field: 'lastTriggeredAt', header: 'Last run', type: 'date', dateTime: true },
    {
      field: 'lastAssignmentId',
      header: 'Last assignment',
      type: 'custom',
      customTemplate: this.lastAssignmentTemplate(),
    },
  ]);

  readonly triggerColumns = computed<TableColumn[]>(() => [
    {
      field: 'name',
      header: 'Name',
      type: 'custom',
      customTemplate: this.triggerIdentityTemplate(),
    },
    {
      field: 'direction',
      header: 'Direction',
      type: 'custom',
      customTemplate: this.triggerDirectionTemplate(),
    },
    {
      field: 'statusLabel',
      header: 'Status',
      type: 'custom',
      customTemplate: this.statusTagTemplate(),
    },
    {
      field: 'executionCount',
      header: 'Executions',
      type: 'custom',
      customTemplate: this.triggerExecutionsTemplate(),
    },
    {
      field: 'lastAssignmentId',
      header: 'Last assignment',
      type: 'custom',
      customTemplate: this.lastAssignmentTemplate(),
    },
  ]);

  readonly subscriptionTableConfig: TableConfig = {
    paginator: false,
    keepOrder: true,
    emptyMessage: 'No subscriptions for this process',
  };

  readonly triggerTableConfig: TableConfig = {
    paginator: false,
    keepOrder: true,
    emptyMessage: 'No triggers for this process',
  };

  readonly dataEffectColumns = computed<TableColumn[]>(() => [
    {
      field: 'commandLabel',
      header: 'Command',
      type: 'custom',
      customTemplate: this.dataEffectCommandTemplate(),
    },
    { field: 'database', header: 'Database', type: 'text' },
    { field: 'table', header: 'Table', type: 'text' },
    {
      field: 'operation',
      header: 'Operation',
      type: 'custom',
      customTemplate: this.dataEffectOperationTemplate(),
    },
    {
      field: 'description',
      header: 'Description',
      type: 'custom',
      customTemplate: this.dataEffectDescriptionTemplate(),
    },
  ]);

  readonly storageEffectColumns = computed<TableColumn[]>(() => [
    {
      field: 'commandLabel',
      header: 'Command',
      type: 'custom',
      customTemplate: this.dataEffectCommandTemplate(),
    },
    { field: 'bucket', header: 'Bucket', type: 'text' },
    { field: 'objectKind', header: 'Object kind', type: 'text' },
    {
      field: 'operation',
      header: 'Operation',
      type: 'custom',
      customTemplate: this.dataEffectOperationTemplate(),
    },
    {
      field: 'description',
      header: 'Description',
      type: 'custom',
      customTemplate: this.dataEffectDescriptionTemplate(),
    },
  ]);

  readonly dataEffectTableConfig: TableConfig = {
    paginator: false,
    keepOrder: true,
    emptyMessage: 'No declared database actions',
  };

  readonly storageEffectTableConfig: TableConfig = {
    paginator: false,
    keepOrder: true,
    emptyMessage: 'No declared object-storage actions',
  };

  ngOnInit(): void {
    this.route.fragment.pipe(takeUntil(this.destroy$)).subscribe(fragment => this.applyTabHash(fragment));
    this.reload$.pipe(
      switchMap(processKey => {
        if (isGeometryProcessKey(processKey)) {
          this.loading.set(false);
          void this.router.navigate(['/admin/process-management/processes']);
          return EMPTY;
        }
        this.loading.set(true);
        this.errorMessage.set('');
        this.notFound.set(false);
        return forkJoin({
          processes: this.assignmentsApi.assignmentsProcessesGet(),
          subscriptions: this.subscriptionsApi.processSubscriptionsGet(),
          triggers: this.triggersApi.processEventTriggersGet(),
          events: this.triggersApi.processEventTriggersEventsGet(),
          notifiableEvents: this.notificationsApi.getEvents().pipe(catchError(() => of([] as NotifiableEventDto[]))),
          notificationRules: this.notificationsApi.getRules().pipe(catchError(() => of([] as NotificationRuleDto[]))),
          notificationTemplates: this.notificationsApi.getTemplates().pipe(catchError(() => of([] as NotificationTemplateDto[]))),
          activity: this.assignmentsApi.assignmentsProcessesActivityGet().pipe(catchError(() => of([]))),
        }).pipe(
          finalize(() => this.loading.set(false)),
        );
      }),
      takeUntil(this.destroy$),
    ).subscribe({
      next: ({ processes, subscriptions, triggers, events, activity, notifiableEvents, notificationRules, notificationTemplates }) => {
        const processKey = this.processKey();
        const catalog = processes as ProcessDefinition[];
        this.processCatalog.set(catalog);
        const definition = findProcessDefinition(catalog, processKey);
        this.process.set(definition);
        this.notFound.set(!definition);
        this.processActivity.set(
          indexProcessActivity(activity, iso => this.datePipe.transform(iso, 'short') ?? iso)
            .get(canonicalProcessKey(processKey)) ?? null,
        );
        this.eventCatalog.set(events as Array<{ key: string; displayName?: string }>);
        this.notifiableEvents.set(notifiableEvents);
        this.notificationRules.set(notificationRules);
        this.notificationTemplates.set(notificationTemplates);
        this.sourceEventKeys.set(
          sourceEventKeysByProcess(triggers).get(canonicalProcessKey(processKey)) ?? [],
        );
        this.remapAssignmentTitles();
        this.subscriptionRows.set(
          filterSubscriptionsByProcessKey(subscriptions, processKey).map(item => {
            const lastAssignmentId = item.lastAssignmentId ?? null;
            return {
              id: item.id ?? '',
              name: item.name ?? '—',
              cronExpression: formatQuartzCronLabel(item.cronExpression ?? '', (key, params) =>
                this.translate.instant(key, params),
              ),
              timeZoneId: item.timeZoneId ? formatTimeZoneLabel(item.timeZoneId) : '—',
              isActive: item.isActive === true,
              statusLabel: item.isActive ? 'Active' : 'Inactive',
              statusSeverity: item.isActive ? 'success' : 'warn',
              nextFireAt: item.nextFireAt,
              lastTriggeredAt: item.lastTriggeredAt,
              lastAssignmentId,
              lastAssignmentUrl: lastAssignmentId
                ? operationDetailsUrl('Assignment', lastAssignmentId)
                : null,
            };
          }),
        );
        this.triggerRows.set(
          filterTriggersByProcessKey(triggers, processKey).map(item => {
            const lastAssignmentId = item.lastTargetAssignmentId
              || item.lastSourceAssignmentId
              || null;
            return {
              id: item.id ?? '',
              name: item.name ?? '—',
              identityLines: buildTriggerIdentityLines({
                eventKey: item.eventKey,
                eventLabel: namedKeyLabel(events as ProcessDefinition[], item.eventKey),
                sourceProcessKey: item.sourceProcessDefinitionKey,
                sourceProcessLabel: namedKeyLabel(catalog, item.sourceProcessDefinitionKey),
                targetProcessKey: item.targetProcessDefinitionKey,
                targetProcessLabel: namedKeyLabel(catalog, item.targetProcessDefinitionKey),
              }),
              direction: triggerDirection(processKey, item),
              isActive: item.isActive === true,
              statusLabel: item.isActive ? 'Active' : 'Inactive',
              statusSeverity: item.isActive ? 'success' : 'warn',
              executionCount: item.executionCount ?? 0,
              successfulExecutionCount: item.successfulExecutionCount ?? 0,
              failedExecutionCount: item.failedExecutionCount ?? 0,
              lastAssignmentId,
              lastAssignmentUrl: lastAssignmentId
                ? operationDetailsUrl('Assignment', lastAssignmentId)
                : null,
            };
          }),
        );
      },
      error: () => {
        this.errorMessage.set('Failed to load process');
        this.process.set(null);
      },
    });

    this.assignmentSearchChanges$.pipe(
      takeUntil(this.destroy$),
    ).subscribe(() => {
      this.resetAssignmentPage();
      this.reloadAssignments();
    });

    this.processRssReload$.pipe(
      tap(() => {
        this.processRss.set(null);
        this.processRssReady.set(false);
      }),
      switchMap(processKey => this.groupsApi.load({
        processDefinitionKey: canonicalProcessKey(processKey),
      }).pipe(
        catchError(() => EMPTY),
      )),
      takeUntil(this.destroy$),
    ).subscribe(groups => {
      const key = canonicalProcessKey(this.processKey()).toLowerCase();
      const group = groups.find(item => (item.processDefinitionKey ?? '').toLowerCase() === key) ?? null;
      this.processRss.set(group ? readAssignmentRss(group) : null);
      this.processRssReady.set(true);
    });

    this.assignmentsReload$.pipe(
      switchMap(() => {
        const processKey = this.processKey();
        if (!processKey || isGeometryProcessKey(processKey)) {
          return EMPTY;
        }
        this.assignmentLoading.set(true);
        const selectedState = this.stateFilter();
        const dates = createdRangeApiBounds(this.createdRange());
        return this.monitoringApi.monitoringOperationsGet(
          'Assignment',
          assignmentOperationsStateQuery(selectedState),
          undefined,
          undefined,
          this.assignmentSearch().trim() || undefined,
          canonicalProcessKey(processKey),
          undefined,
          undefined,
          dates.createdFrom,
          dates.createdTo,
          undefined,
          undefined,
          this.assignmentPage(),
          this.assignmentPageSize(),
          assignmentResultFilterQuery(this.resultFilter()),
        ).pipe(
          catchError((error: unknown) => {
            if (!(error instanceof HttpErrorResponse) || error.status !== 0) {
              this.errorMessage.set('Failed to load assignments');
            }
            return EMPTY;
          }),
          finalize(() => this.assignmentLoading.set(false)),
        );
      }),
      takeUntil(this.destroy$),
    ).subscribe(response => {
      const processKey = this.processKey();
      this.assignmentRows.set(
        filterAssignmentsByProcessKey(response.items, processKey).map(item => this.toAssignmentRow(item, processKey)),
      );
      this.assignmentTotal.set(response.totalCount ?? 0);
      this.assignmentPage.set(response.page ?? this.assignmentPage());
      this.assignmentPageSize.set(response.pageSize ?? this.assignmentPageSize());
      this.assignmentFirst.set((this.assignmentPage() - 1) * this.assignmentPageSize());
    });

    this.route.paramMap.pipe(
      map(params => params.get('processKey') ?? ''),
      distinctUntilChanged(),
      takeUntil(this.destroy$),
    ).subscribe(processKey => {
      if (isGeometryProcessKey(processKey)) {
        void this.router.navigate(['/admin/process-management/processes'], { replaceUrl: true });
        return;
      }
      const canonicalKey = canonicalProcessKey(processKey);
      if (canonicalKey !== processKey) {
        void this.router.navigate(['/admin/process-management/processes', canonicalKey], {
          replaceUrl: true,
          fragment: this.route.snapshot.fragment ?? undefined,
        });
        return;
      }
      this.processKey.set(processKey);
      this.process.set(null);
      this.processCatalog.set([]);
      this.eventCatalog.set([]);
      this.notifiableEvents.set([]);
      this.notificationRules.set([]);
      this.notificationTemplates.set([]);
      this.sourceEventKeys.set([]);
      this.assignmentRows.set([]);
      this.assignmentTotal.set(0);
      this.resetAssignmentPage();
      this.assignmentSearch.set('');
      this.subscriptionRows.set([]);
      this.triggerRows.set([]);
      this.stateFilter.set('all');
      this.resultFilter.set('positive');
      this.createdRange.set(null);
      this.activeTab.set(processPageTabFromHash(this.route.snapshot.fragment));
      this.createVisible.set(false);
      this.createError.set('');
      if (processKey) {
        this.reload$.next(processKey);
        this.reloadAssignments();
        this.processRssReload$.next(processKey);
        this.loadProcessAccess(processKey);
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.reload$.complete();
    this.assignmentsReload$.complete();
    this.processRssReload$.complete();
  }

  reloadAccess(): void {
    const processKey = this.processKey();
    if (processKey) {
      this.loadProcessAccess(processKey);
    }
  }

  private loadProcessAccess(processKey: string): void {
    this.accessGrantsApi.forProcess(processKey).pipe(takeUntil(this.destroy$)).subscribe({
      next: rows => this.accessRows.set(rows ?? []),
      error: () => this.accessRows.set([]),
    });
  }

  onTabChange(tabId: string): void {
    const tab = processPageTabFromHash(tabId === 'assignments' ? null : `tab=${tabId}`);
    this.activeTab.set(tab);
    this.syncTabHash(tab);
  }

  private applyTabHash(fragment: string | null | undefined): void {
    this.applyingTabHash = true;
    try {
      this.activeTab.set(processPageTabFromHash(fragment));
    } finally {
      this.applyingTabHash = false;
    }
    if (!adminHashesEqual(processPageTabHash(this.activeTab()), fragment)) {
      this.syncTabHash(this.activeTab());
    }
  }

  private syncTabHash(tabId: string): void {
    if (this.applyingTabHash) {
      return;
    }
    const fragment = processPageTabHash(tabId);
    if (adminHashesEqual(fragment, this.route.snapshot.fragment)) {
      return;
    }
    const tree = this.router.parseUrl(this.router.url);
    tree.fragment = fragment;
    void this.router.navigateByUrl(tree, { replaceUrl: true });
  }

  selectStateFilter(filter: AssignmentStateFilter): void {
    if (this.stateFilter() === filter) {
      return;
    }
    this.stateFilter.set(filter);
    this.resetAssignmentPage();
    this.reloadAssignments();
  }

  onResultFilterChange(filter: AssignmentResultFilter): void {
    if (this.resultFilter() === filter) {
      return;
    }
    this.resultFilter.set(filter);
    this.resetAssignmentPage();
    this.reloadAssignments();
  }

  onCreatedRangeChange(value: Date[] | Date | null): void {
    const next = Array.isArray(value)
      ? value.filter((entry): entry is Date => entry instanceof Date)
      : null;
    const current = this.createdRange();
    const same = (current ?? []).map(date => date.getTime()).join()
      === (next ?? []).map(date => date.getTime()).join();
    if (same) {
      return;
    }
    this.createdRange.set(next);
    this.resetAssignmentPage();
    this.reloadAssignments();
  }

  onAssignmentPageChange(event: { first?: number; rows?: number }): void {
    const rows = event.rows ?? this.assignmentPageSize();
    const first = event.first ?? 0;
    const page = rows > 0 ? Math.floor(first / rows) + 1 : 1;
    if (page === this.assignmentPage() && rows === this.assignmentPageSize() && first === this.assignmentFirst()) {
      return;
    }
    this.assignmentFirst.set(first);
    this.assignmentPageSize.set(rows);
    this.assignmentPage.set(page);
    this.reloadAssignments();
  }

  onAssignmentPageSizeChange(pageSize: number): void {
    if (pageSize === this.assignmentPageSize()) {
      return;
    }
    this.assignmentPageSize.set(pageSize);
    this.resetAssignmentPage();
    this.reloadAssignments();
  }

  private toAssignmentRow(
    item: MonitoredOperationListItemResponse,
    processKey: string,
  ): AssignmentRow {
    const operationType = item.operationType ?? processKey;
    const outcome = assignmentOutcome({ ...item, operationType });
    return {
      id: item.id ?? '',
      feedName: item.name ?? '',
      operationType,
      name: assignmentRowTitle(item.name, operationType, this.processCatalog()),
      state: item.state ?? '—',
      stateLabel: outcome.skipped ? outcome.label : (item.state ?? '—'),
      stateSeverity: outcome.skipped ? 'secondary' : assignmentStateSeverity(item.state),
      statusAt: assignmentStatusAt(item.state, item.startedAt, item.completedAt),
      createdAt: item.createdAt,
      progressLabel: `${item.progressPercentage ?? 0}%`,
      durationLabel: formatAssignmentDuration(item.durationSeconds),
      ...readAssignmentWorker(item),
      hostLabel: assignmentHostLabel({
        kind: item.kind,
        startedAt: item.startedAt,
        ...readAssignmentHost(item),
      }),
      resultCount: readAssignmentResultCount(item),
      resultTooltip: formatAssignmentResultTooltip(item),
      rss: readAssignmentRss(item),
      error: item.error ?? null,
      canCancel: canCancelAssignment(item.state),
      canRestart: canRestartAssignment(item.state),
    };
  }

  private remapAssignmentTitles(): void {
    const catalog = this.processCatalog();
    this.assignmentRows.update(rows => rows.map(row => ({
      ...row,
      name: assignmentRowTitle(row.feedName, row.operationType, catalog),
    })));
  }

  private resetAssignmentPage(): void {
    this.assignmentPage.set(1);
    this.assignmentFirst.set(0);
  }

  private reloadAssignments(): void {
    this.assignmentsReload$.next();
  }

  reload(): void {
    const processKey = this.processKey();
    if (processKey) {
      this.reload$.next(processKey);
      this.reloadAssignments();
      this.processRssReload$.next(processKey);
    }
  }

  createAssignment(): void {
    if (!this.process()) {
      return;
    }
    this.createError.set('');
    this.startForm()?.reset();
    this.createVisible.set(true);
  }

  onCreateVisibleChange(visible: boolean): void {
    if (!visible && this.createSaving()) {
      return;
    }
    this.createVisible.set(visible);
    if (!visible) {
      this.createError.set('');
    }
  }

  closeCreate(): void {
    this.onCreateVisibleChange(false);
  }

  confirmCreate(): void {
    const processKey = this.processKey();
    const built = this.startForm()?.build();
    if (!processKey || !built || this.createSaving()) {
      return;
    }
    if (!built.ok) {
      this.createError.set(built.error);
      return;
    }
    this.createSaving.set(true);
    this.createError.set('');
    const request: StartAssignmentProcessRequest = { parameters: built.parameters };
    this.assignmentsApi.assignmentsProcessesProcessKeyPost(processKey, request)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.createSaving.set(false)),
      )
      .subscribe({
        next: () => {
          this.createVisible.set(false);
          this.resultFilter.set(assignmentResultFilterAfterCreate());
          this.resetAssignmentPage();
          this.reload();
        },
        error: error => {
          this.createError.set(assignmentStartApiError(error, 'Failed to create assignment'));
        },
      });
  }

  createSubscription(): void {
    const processKey = this.processKey();
    if (!processKey) {
      return;
    }
    void this.router.navigate(['/admin/process-management/subscriptions'], {
      queryParams: { processKey, create: '1' },
    });
  }

  notifyPeople(): void {
    const processKey = this.processKey();
    if (!processKey) {
      return;
    }
    void this.router.navigate([NOTIFY_PEOPLE_PATH], {
      queryParams: notifyPeopleQueryParams({
        eventKey: 'assignment.completed',
        sourceProcessDefinitionKey: processKey,
      }),
    });
  }

  openAssignment(row: AssignmentRow): void {
    if (!row.id) {
      return;
    }
    void this.router.navigateByUrl(operationDetailsUrl('Assignment', row.id));
  }

  openSubscription(row: SubscriptionRow): void {
    if (!row.id) {
      return;
    }
    void this.router.navigate(['/admin/process-management/subscriptions', row.id]);
  }

  openSubscriber(row: { id: string }): void {
    if (!row.id) {
      return;
    }
    void this.router.navigateByUrl(notificationRuleUrl(row.id));
  }

  requestCancellation(row: AssignmentRow, event?: Event): void {
    event?.stopPropagation();
    if (!row.canCancel || !row.id) {
      return;
    }
    this.cancellationAssignmentId.set(row.id);
    this.cancellationVisible.set(true);
  }

  closeCancellation(): void {
    if (this.cancellationSaving()) {
      return;
    }
    this.cancellationVisible.set(false);
    this.cancellationAssignmentId.set(null);
  }

  confirmCancellation(): void {
    const assignmentId = this.cancellationAssignmentId();
    if (!assignmentId || this.cancellationSaving()) {
      return;
    }
    this.cancellationSaving.set(true);
    this.errorMessage.set('');
    this.assignmentsApi.assignmentsAssignmentIdCancelPost(assignmentId)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.cancellationSaving.set(false)),
      )
      .subscribe({
        next: () => {
          this.cancellationVisible.set(false);
          this.cancellationAssignmentId.set(null);
          this.reload();
        },
        error: () => {
          this.errorMessage.set('Failed to cancel assignment');
        },
      });
  }

  requestStopAll(): void {
    if (!this.process() || this.stopAllSaving()) {
      return;
    }
    this.stopAllVisible.set(true);
  }

  closeStopAll(): void {
    if (this.stopAllSaving()) {
      return;
    }
    this.stopAllVisible.set(false);
  }

  requestRestartFailed(): void {
    if (!this.process() || this.restartFailedSaving()) {
      return;
    }
    this.restartFailedVisible.set(true);
  }

  onRestartFailedVisible(visible: boolean): void {
    if (visible) {
      this.restartFailedVisible.set(true);
      return;
    }
    this.closeRestartFailed();
  }

  closeRestartFailed(): void {
    if (this.restartFailedSaving()) {
      return;
    }
    this.restartFailedVisible.set(false);
  }

  confirmRestartFailed(): void {
    const processKey = this.processKey();
    if (!processKey || this.restartFailedSaving()) {
      return;
    }
    this.restartFailedSaving.set(true);
    this.errorMessage.set('');
    this.restartFailedApi.restart(processKey).pipe(
      takeUntil(this.destroy$),
      finalize(() => this.restartFailedSaving.set(false)),
    ).subscribe({
      next: result => {
        this.restartFailedVisible.set(false);
        if (result.skipped > 0) {
          this.errorMessage.set('ORCH_RESTART_FAILED_AND_SKIPPED_PARTIAL');
        }
        this.reload();
      },
      error: () => {
        this.errorMessage.set('ORCH_RESTART_FAILED_AND_SKIPPED_ERROR');
      },
    });
  }

  requestEditCap(): void {
    if (!this.process() || this.capLoading() || this.capSaving()) {
      return;
    }
    this.capLoading.set(true);
    this.capError.set('');
    this.errorMessage.set('');
    this.resourceProfilesApi.orchestrationResourceProfilesGet()
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.capLoading.set(false)),
      )
      .subscribe({
        next: profiles => {
          const profile = findActiveSatelliteProfile(profiles);
          if (!profile?.id) {
            this.errorMessage.set('No active resource profile');
            return;
          }
          const processKey = this.process()?.key ?? this.processKey();
          this.capProfile.set(profile);
          this.capInput.set(overlayCapInput(profile.processConcurrency, processKey));
          this.capVisible.set(true);
        },
        error: () => {
          this.errorMessage.set('Failed to load resource profiles');
        },
      });
  }

  closeEditCap(): void {
    if (this.capSaving()) {
      return;
    }
    this.capVisible.set(false);
    this.capProfile.set(null);
    this.capError.set('');
  }

  requestEditFailure(): void {
    const selected = this.process();
    if (!selected || this.failureSaving()) {
      return;
    }
    this.failureError.set('');
    this.failurePolicy.set(selected.interruptionPolicy || INTERRUPTION_WAIT_FOR_NEXT);
    this.failureResume.set(selected.resumeAttemptLimit ?? 1);
    this.failurePause.set(selected.pauseAfterConsecutiveFailures ?? 0);
    this.failureVisible.set(true);
  }

  onFailureDialogVisible(visible: boolean): void {
    if (visible) {
      this.failureVisible.set(true);
      return;
    }
    this.closeEditFailure();
  }

  closeEditFailure(): void {
    if (this.failureSaving()) {
      return;
    }
    this.failureVisible.set(false);
    this.failureError.set('');
  }

  setFailurePolicy(value: string): void {
    this.failurePolicy.set(value);
  }

  setFailureResume(value: string | number): void {
    this.failureResume.set(Number(value));
  }

  setFailurePause(value: string | number): void {
    this.failurePause.set(Number(value));
  }

  confirmEditFailure(): void {
    const processKey = this.process()?.key;
    if (!processKey || this.failureSaving()) {
      return;
    }
    const policy = this.failurePolicy();
    const resume = this.failureResume();
    const pause = this.failurePause();
    if (policy !== INTERRUPTION_WAIT_FOR_NEXT && policy !== INTERRUPTION_RESUME_SAME) {
      this.failureError.set('Failed to save failure policy');
      return;
    }
    if (!Number.isInteger(resume) || resume < 1 || resume > 10) {
      this.failureError.set('Resume attempts must be a whole number from 1 to 10.');
      return;
    }
    if (!Number.isInteger(pause) || pause < 0 || pause > 30) {
      this.failureError.set('Pause after consecutive failures must be a whole number from 0 to 30.');
      return;
    }
    this.failureSaving.set(true);
    this.failureError.set('');
    this.failurePolicyApi.save(processKey, {
      interruptionPolicy: policy,
      resumeAttemptLimit: resume,
      pauseAfterConsecutiveFailures: pause,
    }).pipe(
      takeUntil(this.destroy$),
      finalize(() => this.failureSaving.set(false)),
    ).subscribe({
      next: () => {
        this.failureVisible.set(false);
        this.reload();
      },
      error: () => {
        this.failureError.set('Failed to save failure policy');
      },
    });
  }

  onCapInput(value: unknown): void {
    this.capInput.set(value == null ? '' : String(value));
  }

  onCapDialogVisible(visible: boolean): void {
    if (visible) {
      this.capVisible.set(true);
      return;
    }
    this.closeEditCap();
  }

  requestEditTags(): void {
    if (!this.process() || this.tagsLoading() || this.tagsSaving()) {
      return;
    }
    this.tagsLoading.set(true);
    this.tagsError.set('');
    this.errorMessage.set('');
    forkJoin({
      profiles: this.resourceProfilesApi.orchestrationResourceProfilesGet(),
      tags: this.workerTagsApi.list().pipe(
        map(rows => ({ ok: true, rows })),
        catchError(() => of({ ok: false, rows: [] as { name: string }[] })),
      ),
    })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.tagsLoading.set(false)),
      )
      .subscribe({
        next: ({ profiles, tags }) => {
          const profile = findActiveSatelliteProfile(profiles);
          if (!profile?.id) {
            this.errorMessage.set('No active resource profile');
            return;
          }
          const processKey = this.process()?.key ?? this.processKey();
          const current = tagSelectionForProcess(readProfileWorkerTags(profile), processKey);
          this.tagsProfile.set(profile);
          this.tagCatalog.set(tags.rows.map(tag => tag.name));
          this.tagsSelected.set(current.selectedTags);
          this.tagsAnyWorker.set(current.anyWorker);
          this.tagsError.set(tags.ok ? '' : 'Failed to load worker tags');
          this.tagsVisible.set(true);
        },
        error: () => {
          this.errorMessage.set('Failed to load resource profiles');
        },
      });
  }

  closeEditTags(): void {
    if (this.tagsSaving()) {
      return;
    }
    this.tagsVisible.set(false);
    this.tagsProfile.set(null);
    this.tagsError.set('');
  }

  onTagsSelected(value: unknown): void {
    this.tagsSelected.set(Array.isArray(value) ? value.map(item => String(item)) : []);
  }

  onTagsAnyWorker(value: boolean): void {
    this.tagsAnyWorker.set(value === true);
  }

  onTagsDialogVisible(visible: boolean): void {
    if (visible) {
      this.tagsVisible.set(true);
      return;
    }
    this.closeEditTags();
  }

  confirmEditTags(): void {
    const profile = this.tagsProfile();
    const processKey = this.process()?.key ?? this.processKey();
    if (!profile?.id || !processKey || this.tagsSaving()) {
      return;
    }
    const tags = applyTagsToOverlay(
      readProfileWorkerTags(profile),
      processKey,
      this.tagsSelected(),
      this.tagsAnyWorker(),
    );
    const saveBody = this.resourceProfileSaveBody(profile, profile.processConcurrency ?? {}, tags);
    if (!saveBody) {
      this.tagsError.set('Failed to save worker tags');
      return;
    }
    this.tagsSaving.set(true);
    this.tagsError.set('');
    this.resourceProfilesApi.orchestrationResourceProfilesProfileIdPut(profile.id, saveBody)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.tagsSaving.set(false)),
      )
      .subscribe({
        next: () => {
          this.tagsVisible.set(false);
          this.tagsProfile.set(null);
          this.reload();
        },
        error: () => {
          this.tagsError.set('Failed to save worker tags');
        },
      });
  }

  confirmEditCap(): void {
    const profile = this.capProfile();
    const processKey = this.process()?.key ?? this.processKey();
    if (!profile?.id || !processKey || this.capSaving()) {
      return;
    }
    const parsed = applyCapToOverlay(profile.processConcurrency, processKey, this.capInput());
    if (parsed.invalid) {
      this.capError.set('Invalid process concurrency cap');
      return;
    }
    const saveBody = this.resourceProfileSaveBody(
      profile,
      parsed.overlay,
      readProfileWorkerTags(profile),
    );
    if (!saveBody) {
      this.capError.set('Failed to save assignment cap');
      return;
    }
    this.capSaving.set(true);
    this.capError.set('');
    this.resourceProfilesApi.orchestrationResourceProfilesProfileIdPut(profile.id, saveBody)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.capSaving.set(false)),
      )
      .subscribe({
        next: () => {
          this.capVisible.set(false);
          this.capProfile.set(null);
          this.reload();
        },
        error: () => {
          this.capError.set('Failed to save assignment cap');
        },
      });
  }

  confirmStopAll(): void {
    const processKey = this.processKey();
    if (!processKey || this.stopAllSaving()) {
      return;
    }
    this.stopAllSaving.set(true);
    this.errorMessage.set('');
    this.assignmentsApi.assignmentsProcessesProcessKeyCancelPost(processKey)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.stopAllSaving.set(false)),
      )
      .subscribe({
        next: () => {
          this.stopAllVisible.set(false);
          this.reload();
        },
        error: () => {
          this.errorMessage.set('Failed to stop assignments');
        },
      });
  }

  requestExecutionHold(): void {
    if (!this.process() || this.executionHoldSaving()) {
      return;
    }
    this.executionHoldVisible.set(true);
  }

  closeExecutionHold(): void {
    if (this.executionHoldSaving()) {
      return;
    }
    this.executionHoldVisible.set(false);
  }

  confirmExecutionHold(): void {
    const processKey = this.processKey();
    if (!processKey || this.executionHoldSaving()) {
      return;
    }
    const resume = this.executionSuspended();
    this.executionHoldSaving.set(true);
    this.errorMessage.set('');
    const request = resume
      ? this.executionHoldApi.resume(processKey)
      : this.executionHoldApi.suspend(processKey);
    request.pipe(
      takeUntil(this.destroy$),
      finalize(() => this.executionHoldSaving.set(false)),
    ).subscribe({
      next: () => {
        this.executionHoldVisible.set(false);
        this.reload();
      },
      error: () => {
        this.errorMessage.set(resume ? 'Failed to resume process' : 'Failed to stop process');
      },
    });
  }

  requestRestart(row: AssignmentRow, event?: Event): void {
    event?.stopPropagation();
    if (!row.canRestart || !row.id) {
      return;
    }
    this.restartAssignmentId.set(row.id);
    this.restartVisible.set(true);
  }

  closeRestart(): void {
    if (this.restartSaving()) {
      return;
    }
    this.restartVisible.set(false);
    this.restartAssignmentId.set(null);
  }

  confirmRestart(): void {
    const assignmentId = this.restartAssignmentId();
    if (!assignmentId || this.restartSaving()) {
      return;
    }
    this.restartSaving.set(true);
    this.errorMessage.set('');
    this.assignmentsApi.assignmentsAssignmentIdRestartPost(assignmentId)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.restartSaving.set(false)),
      )
      .subscribe({
        next: () => {
          this.restartVisible.set(false);
          this.restartAssignmentId.set(null);
          this.reload();
        },
        error: error => {
          this.errorMessage.set(assignmentStartApiError(error, 'Failed to restart assignment'));
        },
      });
  }

  private resourceProfileSaveBody(
    profile: OrchestrationResourceProfileResponse,
    processConcurrency: { [key: string]: number },
    requiredWorkerTags: { [key: string]: string[] },
  ): SaveOrchestrationResourceProfileRequest | null {
    const key = profile.key?.trim();
    const name = profile.name?.trim();
    if (!key || !name) {
      return null;
    }
    // TODO: migrate to api-client when SaveOrchestrationResourceProfileRequest declares requiredWorkerTags.
    return {
      key,
      name,
      isActive: profile.isActive ?? false,
      copernicusDownloadConcurrency: profile.copernicusDownloadConcurrency ?? 1,
      minioReadConcurrency: profile.minioReadConcurrency ?? 1,
      minioWriteConcurrency: profile.minioWriteConcurrency ?? 1,
      gdalConcurrency: profile.gdalConcurrency ?? 1,
      gdalCacheMaxMB: profile.gdalCacheMaxMB ?? 256,
      downloadQueues: this.queueBody(profile.downloadQueues),
      postProcessQueues: this.queueBody(profile.postProcessQueues),
      processConcurrency,
      requiredWorkerTags,
    } as SaveOrchestrationResourceProfileRequest;
  }

  private queueBody(queues?: QueueParallelismRequest): QueueParallelismRequest {
    return {
      critical: queues?.critical ?? 1,
      high: queues?.high ?? 1,
      normal: queues?.normal ?? 1,
      bulk: queues?.bulk ?? 1,
    };
  }
}
